/**
 * Helpers for reading Clerk's `supportedFirstFactors`, which is what drives the
 * identifier-first sign-in form: the user types their email, Clerk answers with
 * the methods that email may use, and only those are offered.
 */

/** The subset of Clerk's first-factor shapes the sign-in form needs. */
export interface SignInFactor {
  strategy: string
  emailAddressId?: string
  safeIdentifier?: string
}

export type SignInFactors = SignInFactor[] | null | undefined

export const PASSWORD_FACTOR = 'password' as const
export const EMAIL_CODE_FACTOR = 'email_code' as const
export const PASSKEY_FACTOR = 'passkey' as const
export const ENTERPRISE_SSO_FACTOR = 'enterprise_sso' as const

export const hasFactor = (factors: SignInFactors, strategy: string): boolean =>
  Boolean(factors?.some((factor) => factor.strategy === strategy))

/** Clerk reports which email address the code would go to, so it has to be passed back on prepare. */
export const getEmailCodeFactor = (factors: SignInFactors): SignInFactor | undefined =>
  factors?.find((factor) => factor.strategy === EMAIL_CODE_FACTOR)

/**
 * True when single sign-on is the only thing this identifier can do. There is
 * nothing to choose between, so the form skips the method step and redirects.
 */
export const isEnterpriseSSOOnly = (factors: SignInFactors): boolean =>
  hasFactor(factors, ENTERPRISE_SSO_FACTOR) &&
  ![PASSWORD_FACTOR, EMAIL_CODE_FACTOR, PASSKEY_FACTOR].some((strategy) => hasFactor(factors, strategy))

/** True when the identifier has no way to sign in that this form knows how to render. */
export const hasNoSupportedFactor = (factors: SignInFactors): boolean =>
  Boolean(factors) &&
  ![PASSWORD_FACTOR, EMAIL_CODE_FACTOR, PASSKEY_FACTOR, ENTERPRISE_SSO_FACTOR].some((strategy) =>
    hasFactor(factors, strategy)
  )
